import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineMapPin,
  HiOutlineBanknotes,
  HiOutlineClipboardDocumentList,
} from 'react-icons/hi2';
import { useTenantStore } from '../../stores/tenantStore';
import { listPermits } from '../../modules/spatial/api';
import { listBudgets } from '../../modules/finance/api';
import { listCases } from '../../modules/admin/api';
import { StatCard } from '../shared/StatCard';
import styles from './ModuleWidgets.module.css';

interface ModuleStats {
  spatial: { total: number; atAuflage: number } | null;
  finance: { label: string; status: string } | null;
  admin: { open: number; overdue: number } | null;
}

export function ModuleWidgets() {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const features = useTenantStore((s) => s.features);
  const [stats, setStats] = useState<ModuleStats>({ spatial: null, finance: null, admin: null });

  const spatialEnabled = features.module_spatial;
  const financeEnabled = features.module_finance;
  const adminEnabled = features.module_admin;

  useEffect(() => {
    if (!spatialEnabled && !financeEnabled && !adminEnabled) return;

    const promises: Promise<void>[] = [];

    if (spatialEnabled) {
      promises.push(
        listPermits({ limit: 200 })
          .then((permits) => {
            const atAuflage = permits.filter((p) => p.stage === 'auflage').length;
            setStats((s) => ({ ...s, spatial: { total: permits.length, atAuflage } }));
          })
          .catch(() => {})
      );
    }

    if (financeEnabled) {
      promises.push(
        listBudgets({ limit: 1 })
          .then((budgets) => {
            if (budgets.length > 0) {
              const b = budgets[0]!;
              setStats((s) => ({ ...s, finance: { label: `FY${b.fiscal_year}`, status: b.status } }));
            }
          })
          .catch(() => {})
      );
    }

    if (adminEnabled) {
      promises.push(
        listCases({ status: 'open', limit: 200 })
          .then((cases) => {
            const now = new Date();
            const overdue = cases.filter((c) => c.due_date && new Date(c.due_date) < now).length;
            setStats((s) => ({ ...s, admin: { open: cases.length, overdue } }));
          })
          .catch(() => {})
      );
    }

    Promise.allSettled(promises);
  }, [spatialEnabled, financeEnabled, adminEnabled]);

  if (!spatialEnabled && !financeEnabled && !adminEnabled) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{t('dashboard:moduleOverview', 'Module Overview')}</h3>
      <div className={styles.widgetRow}>
        {spatialEnabled && stats.spatial && (
          <StatCard
            label={t('dashboard:permitPipeline', 'Permit Pipeline')}
            value={stats.spatial.total}
            sub={stats.spatial.atAuflage > 0 ? `${stats.spatial.atAuflage} ${t('dashboard:atAuflage', 'at public display')}` : undefined}
            icon={<HiOutlineMapPin />}
            onClick={() => navigate('/app/spatial/permits')}
          />
        )}
        {financeEnabled && stats.finance && (
          <StatCard
            label={t('dashboard:latestBudget', 'Latest Budget')}
            value={stats.finance.label}
            sub={stats.finance.status}
            icon={<HiOutlineBanknotes />}
            onClick={() => navigate('/app/finance/budgets')}
          />
        )}
        {adminEnabled && stats.admin && (
          <StatCard
            label={t('dashboard:openCases', 'Open Cases')}
            value={stats.admin.open}
            sub={stats.admin.overdue > 0 ? `${stats.admin.overdue} ${t('dashboard:overdue', 'overdue')}` : undefined}
            icon={<HiOutlineClipboardDocumentList />}
            color={stats.admin.overdue > 0 ? '#f87171' : undefined}
            onClick={() => navigate('/app/administration/cases')}
          />
        )}
      </div>
    </div>
  );
}
